import { type PropsWithChildren, useMemo } from 'react';
import { type DefaultTheme, ThemeProvider } from 'styled-components';
import { useWindowWidth } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { defaultTheme } from '../constants';
import {
  defaultFormPopupStyles,
  FORM_POPUP_MEDIUM_BREAKPOINT,
  FORM_POPUP_SMALL_BREAKPOINT,
  mediumFormPopupStyles,
  smallFormPopupStyles
} from './constants';
import { getFormAppearanceSettings } from './utils';

const getFormPopupStyles = (windowWidth: number) => {
  if (windowWidth < FORM_POPUP_SMALL_BREAKPOINT) {
    return smallFormPopupStyles;
  }

  if (windowWidth < FORM_POPUP_MEDIUM_BREAKPOINT) {
    return mediumFormPopupStyles;
  }

  return defaultFormPopupStyles;
};

export function FormThemeProvider({ children }: PropsWithChildren) {
  const {
    formBackgroundColor,
    formAccentColor,
    formTextColor,
    formErrorColor,
    formSuccessColor
  } = useSettings();
  const windowWidth = useWindowWidth();

  const { fieldHeadingFontSize, ...popup } = useMemo(() => {
    const formPopupStyles = getFormPopupStyles(windowWidth);

    return {
      ...formPopupStyles,
      background: {
        type: 'color' as const,
        color: formBackgroundColor
      }
    };
  }, [windowWidth, formBackgroundColor]);

  const form = useMemo(() => {
    const formAppearanceSettings = getFormAppearanceSettings({
      formAccentColor,
      formTextColor,
      formErrorColor,
      formSuccessColor
    });

    const headingFontStyle = {
      fontSize: fieldHeadingFontSize,
      fontWeight: 'bold' as const
    };

    return {
      ...formAppearanceSettings,
      fieldHeadingFontStyle: headingFontStyle,
      submitMessageTitleFontStyle: headingFontStyle
    };
  }, [
    formAccentColor,
    formTextColor,
    fieldHeadingFontSize,
    formErrorColor,
    formSuccessColor
  ]);

  const theme: DefaultTheme = useMemo(() => {
    return {
      ...defaultTheme,
      popup,
      form
    };
  }, [popup, form]);

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}
